// flow
import { messageFormat } from '@clubmed/utils';
import { get, omit } from 'lodash';

import { t } from 'Src/i18n';
import { BE_TRACKING, TRACK_EVENT_NAMES } from 'Tracking/constants';
import * as TrackingApi from 'Tracking/tracking-api';
import { getTrackingRoomType } from 'Tracking/utils';
import {
  sendTransaction as sendAbTastyTransaction,
  sendItem as sendAbTastyItem,
} from 'Components/utils/ab-tasty/abTasty.utils';
import { getExclusiveCollectionFromAccomodation } from 'Views/booking/BookingEngine.utils';
import * as trackingStorageApi from 'Tracking/tracking-storage-api';
import { asGqlData } from 'Src/types/view-helpers';
import { JOYVIEW_UPSELL_RON_PACKAGEID } from 'Utils/payment/constants';
import { STATUS_CODE_200, STATUS_CODE_409 } from 'Utils/status-code/statusCode.constants';
import { BE_GA_DEFAULT_PAYLOAD } from 'Views/booking/constants';

const upsellQuatityScheduleReducer = (upsellQuantityAttendee, schedule) => {
  if (!schedule.isPartiallyRented) {
    return upsellQuantityAttendee;
  }
  if (schedule.timeSlots && schedule.timeSlots.length > 0) {
    return upsellQuantityAttendee + schedule.timeSlots.filter(({ isRented }) => isRented).length;
  }
  return upsellQuantityAttendee + 1;
};
const upsellQuantityAttendeeReducer = (upsellQuantity, attendee) => {
  return upsellQuantity + attendee.schedules.reduce(upsellQuatityScheduleReducer, 0);
};
export const getUpSellQuantity = (pricingInfo) => {
  if (['TRANSFER', 'INSURANCE'].includes(pricingInfo.type)) {
    return 1;
  }
  if (!pricingInfo.isPartiallyRented) {
    return 0;
  }
  // new Format ski wellness ...
  if (pricingInfo.attendees) {
    return pricingInfo.attendees.reduce(upsellQuantityAttendeeReducer, 0);
  }
  // old format ie : childcare etc ..
  return pricingInfo.schedules.reduce(
    (totalSchedules, { attendees }) =>
      totalSchedules +
      attendees.reduce((totalAttendees, { isRented }) => totalAttendees + (isRented ? 1 : 0), 0),
    0,
  );
};

export const upSellTypeMapping = {
  CHILDCARE: 'childcare',
  EXCURSION: 'excursion',
  LAND_SPORT: 'landSport',
  RENTAL: 'ski',
  TRANSFER: 'transfer',
  WATER_SPORT: 'waterSport',
  WINTER_SPORT: 'winterSport',
  WELLNESS: 'wellness',
  INSURANCE: 'insurance',
};

export const getDefaultUpsellTrackingProps = (appContext, proposal) => {
  return {
    ...BE_GA_DEFAULT_PAYLOAD,
    categoryGA: 'upsellTransaction',
    ...(appContext.isCmta && {
      agenceType: appContext.cmta.isDelCredere ? 'Ducroire' : 'NonDucroire',
    }),
    ...(proposal?.packageId !== JOYVIEW_UPSELL_RON_PACKAGEID && { upsellTransaction: 1 }),
    upsell: 'yes',
    actionGA: proposal?.resort?.destination?.country,
    roomType: getTrackingRoomType(proposal.accommodations),
    labelGA: proposal?.resort?.id,
    insuranceSubscribed: proposal.additionalServices.insurance?.some(
      ({ pricingInfo }) => pricingInfo.isPartiallyRented,
    ),
    transfer: proposal?.additionalServices?.transfers?.some(
      ({ pricingInfo }) => pricingInfo.isPartiallyRented,
    )
      ? 'yes'
      : 'no',
  };
};

export const trackUpsell = (trackEvent, proposal, upsellRevenue) => {
  const storage = asGqlData<{
    roomFilters?: Array<Record<string, unknown>>;
    roomOptions?: Record<string, unknown>;
  }>(trackingStorageApi.get(BE_TRACKING));
  const roomFilters = storage.roomFilters || [];
  const roomOptions = storage.roomOptions || {};
  const tracking = {
    upsell_revenue: upsellRevenue,
    upsell_category: 'room',
    upsell_name: proposal.accommodations[0].id,
    upsell_quantity: proposal.accommodations.reduce(
      (quantity, accommodation) => quantity + accommodation.quantity,
      0,
    ),
  };

  if (
    'upsell' in roomOptions &&
    roomOptions.upsell === 'yes' &&
    roomOptions.proposalId === proposal.id
  ) {
    Object.assign(tracking, {
      upsell_revenue: roomOptions.upsellRevenue,
      upsell_category: roomOptions.upsellCategory,
      upsell_name: roomOptions.upsellName,
      upsell_quantity: roomOptions.upsellQuantity,
    });
  }

  if (roomFilters.length) {
    roomFilters
      .filter((roomFilter) => roomFilter.proposalId === proposal.id)
      .forEach(
        (filter: {
          upsellRevenue?: number;
          upsellCategory?: string;
          upsellName?: string;
          upsellQuantity?: number;
          proposalId?: string;
        }) => {
          if ((filter.upsellRevenue ?? 0) > 0) {
            trackEvent(
              TRACK_EVENT_NAMES.UPSELL_TRANSACTION,
              omit(
                {
                  ...tracking,
                  upsell_revenue: filter.upsellRevenue,
                  upsell_category: filter.upsellCategory,
                  upsell_name: filter.upsellName,
                },
                'proposalId',
              ),
            );
          }
        },
      );
  }

  if (tracking.upsell_revenue > 0) {
    trackEvent(TRACK_EVENT_NAMES.UPSELL_TRANSACTION, omit(tracking, 'proposalId'));
  }

  trackingStorageApi.set({
    ...asGqlData<Record<string, unknown>>(storage),
    roomFilters: [],
    roomOptions: {},
  });
};

export const trackJoyViewUpsell = (trackEvent, proposal, joyviewUpSell) => {
  trackEvent(TRACK_EVENT_NAMES.UPSELL_TRANSACTION, {
    upsell_category: 'joyview',
    upsell_name: proposal.packageId,
    upsell_quantity: 1,
    upsell_revenue: joyviewUpSell,
  });
};

export const trackAbTasty = (proposal, _appContext, bookingId) => {
  const { price, resort, accommodations } = proposal;
  const exclusiveCollection = getExclusiveCollectionFromAccomodation(
    resort?.hasECSpace,
    accommodations?.[0]?.exclusiveCollection,
  );

  sendAbTastyTransaction({
    affiliation: 'purchase',
    currency: price?.total?.currency || '',
    id: bookingId,
    revenue: price?.total?.value,
  });

  sendAbTastyItem({
    id: bookingId,
    name: resort?.name || '',
    price: price?.total?.value,
    code: resort?.id || '',
    category: exclusiveCollection,
  });
};

export const trackAdditionnalServicesRented = (trackEvent, additionalServicesRented) => {
  return additionalServicesRented
    .map((additionalService, index, additionalServices) => {
      /* in case of services containing time slots each time slot is a unique service
       so to calculate the total amount and the quantity we need to iterate over
       each service sharing the same productInfo.id
      */
      const existingServiceIndex = additionalServices.findIndex(
        (service) => service.productInfo.id === additionalService.productInfo.id,
      );

      if (existingServiceIndex && existingServiceIndex !== index) {
        const existingService = additionalServices[existingServiceIndex];
        existingService.pricingInfo.schedules = [
          ...existingService.pricingInfo.schedules,
          ...additionalService.pricingInfo.schedules,
        ];

        existingService.pricingInfo.rentedAmount.value +=
          additionalService.pricingInfo.rentedAmount.value;
      } else {
        return additionalService;
      }
    })
    .filter(Boolean)
    .forEach(({ pricingInfo, productInfo }) => {
      pricingInfo.rentedAmount.value > 0 &&
        trackEvent(TRACK_EVENT_NAMES.UPSELL_TRANSACTION, {
          upsell_category: upSellTypeMapping[pricingInfo.type],
          upsell_name: productInfo.id,
          upsell_revenue: pricingInfo.rentedAmount.value,
          upsell_quantity: getUpSellQuantity(pricingInfo),
        });
    });
};

export const computeTransferTrackingValue = (proposal, selectedTransfers) => {
  if (proposal.transports) {
    return 'yes with transport';
  }

  if (selectedTransfers.length > 0) {
    return 'yes without transport';
  }
  return 'no';
};

type OptionQueryParams = Partial<{
  futureTravelCreditOption: string;
}> &
  QueryParams;

type OptionUrlParams = UrlParams;

export const eventTracking = (
  optionID: string,
  appContext: AppContext,
  queryParams: OptionQueryParams,
) => {
  const data = trackingStorageApi.get(BE_TRACKING);
  TrackingApi.setDataLayer(
    {
      categoryGA: 'option',
      labelGA: get(data, 'basePriceParticipant'),
      siteSection: 'Booking Engine',
      ...(optionID ? { actionGA: optionID } : {}),
    },
    appContext,
  );

  if (queryParams.futureTravelCreditOption === 'true') {
    TrackingApi.setDataLayer(
      {
        categoryGA: 'optionFTC',
        labelGA: optionID,
        actionGA: 'confirmation',
        siteSection: 'Booking Engine',
      },
      appContext,
    );
  }
};

export const getConfirmationProps = ({
  location,
  bookingId,
  config,
  proposal,
  isCmta,
  isSeller,
  isNotDelCredere,
  hasSaveOption,
  expirationTime,
  isReliable,
}: {
  location: RouterLocation<OptionUrlParams, OptionQueryParams>;
  bookingId: string;
  config: CMSContent;
  proposal: Proposal;
  isCmta: boolean;
  isSeller: boolean;
  isNotDelCredere: boolean;
  hasSaveOption: boolean;
  expirationTime?: string;
  isReliable?: boolean;
}) => {
  const { urlParams: params, queryParams } = location;

  const resortName = get(proposal, 'resort.name');
  const {
    option: {
      confirmation: {
        failureImmersiveTitle,
        failureDescription,
        successTitle,
        successDescription2,
        aBookingAlreadyExistsTitle,
        aBookingAlreadyExistsDescription,
        aBookingAlreadyExistsDescription2,
        itemOfTheBookingNotAvailableTitle,
        itemOfTheBookingNotAvailableDescription,
      },
    },
    partners,
    confirmation: { failure },
  } = config.booking;

  const isNotDelCredereCmta = isCmta && isNotDelCredere;
  const failureTitle = messageFormat(failure.headSentence).format({
    resortName,
  });
  const failureMessage = failure.notCompletedMessage;
  const isFtcOption = queryParams.futureTravelCreditOption === 'true';

  switch (parseInt(params.status, 10)) {
    case STATUS_CODE_200:
      const defaultSuccessProps = {
        type: 'OPTION',
        status: 'SUCCESS',
        bookingId,
        expirationTime,
        isReliable,
      };
      if (isFtcOption) {
        return {
          ...defaultSuccessProps,
          title: t('booking.summary.futureTravelCredit.confirmation.title'),
          message: t('booking.summary.futureTravelCredit.confirmation.information'),
        };
      }

      if (isNotDelCredereCmta && !hasSaveOption) {
        return {
          ...defaultSuccessProps,
          title: t('booking.partners.confirmation.noDelcredere.headSentence'),
          message: t('booking.partners.confirmation.noDelcredere.bookingCompleteAdditionalText'),
        };
      }

      if (isCmta) {
        return {
          ...defaultSuccessProps,
          title: messageFormat(successTitle).format({
            resortName,
          }),
          message: `${partners.option.confirmation.successDescription} ${partners.phoneNumber}`,
        };
      }

      return {
        type: 'OPTION',
        status: 'SUCCESS',
        title: messageFormat(successTitle).format({
          resortName,
        }),
        message: successDescription2,
        bookingId,
        expirationTime,
        isReliable,
      };
    case STATUS_CODE_409:
      // BOOKING_ALREADY_EXISTS else BOOKING_UNAVAILABLE
      const aBookingAlreadyExistsMessageType = isSeller
        ? aBookingAlreadyExistsDescription
        : aBookingAlreadyExistsDescription2;

      return params.error_code === 'BOOKING_ALREADY_EXISTS'
        ? {
            bookingId,
            status: 'FAILURE',
            type: 'OPTION',
            title: isNotDelCredereCmta ? failureTitle : aBookingAlreadyExistsTitle,
            message: isNotDelCredereCmta ? failureMessage : aBookingAlreadyExistsMessageType,
          }
        : {
            bookingId,
            status: 'FAILURE',
            type: 'OPTION',
            title: isNotDelCredereCmta ? failureTitle : itemOfTheBookingNotAvailableTitle,
            message: isNotDelCredereCmta ? failureMessage : itemOfTheBookingNotAvailableDescription,
          };
    default:
      return {
        type: 'OPTION',
        status: 'FAILURE',
        title: isNotDelCredereCmta
          ? failureTitle
          : messageFormat(failureImmersiveTitle).format({
              resortName,
            }),
        message: isNotDelCredereCmta ? failureMessage : failureDescription,
        bookingId,
      };
  }
};
